import {Card} from '@ezcater/tapas';
import Image from 'next/image';
import {twMerge} from 'tailwind-merge';

import {type RestaurantTileContent} from '@/components/features/home/HomeRoot/CollectionOfFilters/CollectionOfFilters';
import useTracking from '@/hooks/useTracking';
import {SEARCH_NEW_PATH} from '@/paths';
import {compilePath} from '@/utils';
import {geocodedAddressResultToStartNewSearchAddressParams} from '@/utils/geocodedAddressResult';
import {type GeocodedAddressResult} from '@/utils/googleAutocomplete';

type RestaurantSearchTileProps = {
  item: RestaurantTileContent;
  cardHeader: string;
  tilePosition: number;
  recommendedDefaultAddress?: GeocodedAddressResult | null;
  variant?: 'photo' | 'logo';
};

const RestaurantSearchTile: React.FC<RestaurantSearchTileProps> = ({
  item,
  cardHeader,
  tilePosition,
  recommendedDefaultAddress,
  variant = 'photo',
}) => {
  const {trackClick} = useTracking();

  const href = compilePath(
    SEARCH_NEW_PATH,
    {},
    {
      ...geocodedAddressResultToStartNewSearchAddressParams(recommendedDefaultAddress ?? undefined),
      keyword: item.name,
    },
    {skipEmptyString: true, skipNull: true},
  );

  const handleClick = () => {
    trackClick('collection-of-filters-restaurant-tile', {
      misc_json: JSON.stringify({
        id: item.id,
        keyword: item.name,
        cardHeader,
        tilePosition,
      }),
    });
  };

  return (
    <Card
      className={twMerge(
        'h-[120px] w-[134px] overflow-hidden rounded-2xl p-0 shadow-none transition-shadow focus-within:ring-2 focus-within:ring-peppercorn-800 hover:shadow-lg desktop924:h-[152px] desktop924:w-[168px] desktop924:rounded-3xl',
        variant === 'logo' ? 'border border-peppercorn-100 bg-white' : 'border-0 bg-transparent',
      )}
    >
      <a
        aria-label={item.name}
        className="relative block h-full w-full overflow-hidden no-underline hover:no-underline focus-visible:outline-none"
        data-testid={`collection-of-filters-restaurant-tile-${item.id}`}
        href={href}
        onClick={handleClick}
      >
        {variant === 'logo' ? (
          <div className="flex h-full w-full items-center justify-center">
            <Image
              alt=""
              src={item.imageUrl}
              width={129}
              height={54}
              className="h-auto w-[100px] object-contain desktop924:w-[129px]"
              sizes="(max-width: 923px) 100px, 129px"
            />
          </div>
        ) : (
          <>
            <Image
              alt=""
              src={item.imageUrl}
              fill
              className="object-cover"
              sizes="(max-width: 768px) 40vw, 15vw"
            />
            {item.logoImageUrl && (
              <div className="absolute bottom-[11px] left-[10px] flex h-[32px] w-[58px] items-center justify-center overflow-hidden rounded-lg border border-peppercorn-100 bg-white shadow-md desktop924:h-12 desktop924:w-20 desktop924:rounded-2xl">
                <Image
                  alt=""
                  src={item.logoImageUrl}
                  width={60}
                  height={25}
                  className="h-[18.125px] w-[43.5px] object-contain desktop924:h-[25px] desktop924:w-[60px]"
                  sizes="(max-width: 923px) 44px, 60px"
                />
              </div>
            )}
          </>
        )}
      </a>
    </Card>
  );
};

export default RestaurantSearchTile;
